import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import { XStack } from '@/components/ui/layout/layout';

import { AgentMark } from '../components/agent-mark';

import { useEditPersonMutation } from './edits.query';
import { InlineTextField } from './inline-text-field';

import type { WikiPersonDetail } from '../people-wiki.types';

/** The name of a person as a heading, with a button that edits it in place. */
export const NameEditor = ({
  person,
  startRenaming,
}: {
  person: WikiPersonDetail;
  startRenaming: boolean;
}) => {
  const {
    mutate: editPerson,
    variables: pendingChanges,
    isPending,
  } = useEditPersonMutation(person.id);
  // While a rename is on its way, show the new name, so the old one does not come back for a moment
  const name = (isPending && pendingChanges?.name) || person.name;
  const [isRenaming, setIsRenaming] = useState(startRenaming);

  return isRenaming ? (
    <InlineTextField
      value={name}
      label="Name"
      maxLength={255}
      required
      autoFocus
      className="text-xl text-foreground"
      onSave={(name, revert) => editPerson({ name }, { onError: revert })}
      onFinish={() => setIsRenaming(false)}
    />
  ) : (
    <XStack className="items-center gap-1.5">
      <h1 className="min-w-0 text-xl text-foreground">{name}</h1>
      <IconButton
        size="xs"
        icon={Icons.edit}
        type="button"
        aria-label="Edit name"
        tooltip="Edit name"
        onClick={() => setIsRenaming(true)}
      />
      {person.source === 'agent' && <AgentMark />}
    </XStack>
  );
};
